import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReportControllerService, WbhReportRequest } from "openapi";
import authorizedCall from "features/common/utils/authorizedCall";
import { useUserGrantsContext } from "features/user/hooks/useUserGrantsContext";
import { checkOperationPermission } from "features/auth/utils/checkOperationPermission";
import { StatisticsModalProps } from "./StatisticsModal";
import { AlertColor } from "@mui/material";

export interface useStatisticsModalArgs {
  statisticsReportType: WbhReportRequest;
  reconciliationReportType: WbhReportRequest;
  onClose: StatisticsModalProps["onClose"];
}

export const useStatisticsModal = ({
  onClose,
  statisticsReportType,
  reconciliationReportType,
}: useStatisticsModalArgs) => {
  const [isExecutingRec, setIsExecutingRec] = useState(false);
  const [executeRecError, setExecuteRecError] = useState("");
  const [isExecutingStat, setIsExecutingStat] = useState(false);
  const [executeStatError, setExecuteStatError] = useState("");
  const [noReportCreated, setNoReportCreated] = useState("");

  const { userGrants } = useUserGrantsContext();
  const navigate = useNavigate();

  const pageUrl = `${
    statisticsReportType.actionType === "INTERNAL" ? "internal" : "external"
  }-statistics`;

  const secondPageUrl = `${
    reconciliationReportType.actionType === "INTERNAL" ? "internal" : "external"
  }-reconciliation`;

  const handleClose = () => {
    onClose();
    setExecuteRecError("");
  };

  const handleStartRec = () => {
    setExecuteRecError("");
    setIsExecutingRec(true);

    authorizedCall(
      ReportControllerService.createReport,
      {
        requestBody: reconciliationReportType,
      },
      true
    )
      .then((res) => {
        const reportId = res.reportId;
        setIsExecutingRec(false);
        if (!reportId) {
          setNoReportCreated(
            "Reconciliation done, there are no discrepancies."
          );
        } else {
          navigate(`${secondPageUrl}/${reportId}`);
        }
      })
      .catch((error) => {
        setIsExecutingRec(false);

        if (error.status === 504) {
          setExecuteRecError(
            "Process is taking more time than expected. Please check export log after few minutes."
          );
        } else {
          setExecuteRecError(error?.body?.message || error?.message);
        }
      });
  };

  const handleStartStat = () => {
    setExecuteRecError("");
    setIsExecutingStat(true);

    authorizedCall(
      ReportControllerService.createReport,
      {
        requestBody: statisticsReportType,
      },
      true
    )
      .then((res) => {
        const reportId = res.reportId;
        setIsExecutingStat(false);
        navigate(`${pageUrl}/${reportId}`);
      })
      .catch((error) => {
        setIsExecutingStat(false);
        setExecuteStatError(error?.body?.message || error?.message);
      });
  };

  const componentTypeMap = {
    INTERNAL: "Internal List with Host-File (intern)",
    EXTERNAL: "Internal List with HZ-File (extern)",
  };

  // TODO: check why is actionType with enum INTERNAL and EXTERNAL and not type
  const reconciliationIsAllowed =
    userGrants &&
    checkOperationPermission({
      operation: "E",
      component: componentTypeMap[reconciliationReportType.actionType],
      //component: componentTypeMap[reconciliationReportType.actionType],
      func:
        reconciliationReportType.actionType === "INTERNAL"
          ? "RECON_INT"
          : "RECON_EXT",
      userGrants,
    });

  const statisticsIsAllowed =
    userGrants &&
    checkOperationPermission({
      operation: "E",
      component: componentTypeMap[statisticsReportType.actionType],
      func:
        statisticsReportType.actionType === "INTERNAL"
          ? "STAT_INT"
          : "STAT_EXT",
      userGrants,
    });

  const reconciliationError = executeRecError
    ? executeRecError
    : noReportCreated
    ? noReportCreated
    : "";

  const error = "error" as AlertColor;
  const info = "info" as AlertColor;

  const severityMessage = executeRecError
    ? error
    : noReportCreated
    ? info
    : undefined;

  return {
    isExecutingRec,
    executeRecError,
    isExecutingStat,
    executeStatError,
    handleStartRec,
    handleStartStat,
    handleClose,
    reconciliationIsAllowed,
    statisticsIsAllowed,
    reconciliationError,
    severityMessage,
  };
};
